import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { SolEstimatePricingMode, SolLaborEscalationType } from '../../../models/enum';
import { SolEstimateTableRowBase } from '../../../models/sol-estimate-table/SolEstimateTableRowBase';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MultilingualCurrencyPipe } from "../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe";
import { MatInputModule } from '@angular/material/input';
import { NumericMaskDirective } from '../../../shared/numeric/numeric-mask.directive';
import { EscalationData, EstimateTab } from '../../../models/dtos';
import { SolMaterialEscalationDialogDataModel } from '../../../models/sol-estimate-table/SolMaterialEscalationDialogDataModel';
import { SolEstimateTableCommonFunctionsService } from '../../../services/sol-estimate-table-common-functions/sol-estimate-table-common-functions.service';
import { CommonDataService } from '../../../services/common-data.service';
import { DecimalPrecisionPipe } from "../../../pipes/decimal-precision-pipe/decimal-precision.pipe";
import { CONSTANTS } from '../../../Constants/Constants';
import { LaborSellPriceFlag, LABOR_CATEGORY_BY_LABEL, LABOR_SELL_PRICE_FLAGS } from '../../../services/estimate/estimate-last-modified-sell-price.service';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-sol-estimate-table-labor-cost-row',
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    CommonModule,
    TranslatePipe,
    ReactiveFormsModule,
    NumericMaskDirective,
    MultilingualCurrencyPipe,
    DecimalPrecisionPipe
],
  templateUrl: './sol-estimate-table-labor-cost-row.component.html',
  styleUrl: './sol-estimate-table-labor-cost-row.component.scss'
})
export class SolEstimateTableLaborCostRowComponent implements OnInit, OnDestroy {

  @Input() isMarkUpSelected: boolean = false;
  @Input() sellAsPricingMode: SolEstimatePricingMode = SolEstimatePricingMode.Line_Item;
  @Input() laborCostRowForm!: FormGroup<SolEstimateTableRowBase>;
  @Input() rowLabel: string = '';

  destroy$: Subject<void> = new Subject<void>();

  solEstimatePricingMode = SolEstimatePricingMode
  
  /** Live EstimateTab, read directly by the template so flag updates are picked up in place. */
  estimateTab: EstimateTab | null = null;

  /** EstimateTab flag name for this row's sell-price-last-modified status. */
  sellPriceFlag: LaborSellPriceFlag | null = null;
  

  get isEstimateSapSubmitted(): boolean {
    return this.solEstimateTableCommonFunctionsService.isEstimateSapSubmitted;
  }

  constructor(
    private readonly commonDataService: CommonDataService,
    public solEstimateTableCommonFunctionsService: SolEstimateTableCommonFunctionsService
  ) { }

  ngOnInit(): void {
    if(!this.laborCostRowForm){
      return;
    }
    this.commonDataService.projectObjectDataAvailable$
    .pipe(takeUntil(this.destroy$))
    .subscribe((isAvailable: boolean) => {
      if (isAvailable) {
        this.estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab ?? null;
        const category = LABOR_CATEGORY_BY_LABEL[this.rowLabel];
        if (category) {
          this.sellPriceFlag = LABOR_SELL_PRICE_FLAGS[category];
        }
      }
    });
  }

  openEscalationDialog(sectionLabel: string): void {
    if (this.commonDataService.isReadOnlyMode()) {
      return;
    }

    // open estimate API Integration
    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    const escalationRates = estimateMetaData.escalationRates;
    let escalationDialogData: SolMaterialEscalationDialogDataModel = {
      escalationData: new EscalationData(),
      sectionLabel: sectionLabel,
      escalationType: CONSTANTS.LABOR,
      applyMaterialEscalationToAll: escalationRates.applyMaterialEscalationToAll,
      applyLaborEscalationToAll: escalationRates.applyLaborEscalationToAll,
      isEscalationDisabled: this.isEstimateSapSubmitted
    }
    switch (sectionLabel) {
      case SolLaborEscalationType.Project_Management:
        escalationDialogData.escalationData = escalationRates.pmLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolLaborEscalationType.Engineering:
        escalationDialogData.escalationData = escalationRates.engLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolLaborEscalationType.CYBER_SECURITY_ENGINEERING:
        escalationDialogData.escalationData = escalationRates.cengLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolLaborEscalationType.ENERGY_ENGINEERING:
        escalationDialogData.escalationData = escalationRates.eengLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolLaborEscalationType.ELECTRICAL_ENGINEERING:
        escalationDialogData.escalationData = escalationRates.lengLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolLaborEscalationType.NETWORK_ENGINEERING:
        escalationDialogData.escalationData = escalationRates.nengLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolLaborEscalationType.QUALIFICATION_ENGINEERING:
        escalationDialogData.escalationData = escalationRates.qengLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolLaborEscalationType.ELECTRICAL:
        escalationDialogData.escalationData = escalationRates.elecLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolLaborEscalationType.MECHANICAL:
        escalationDialogData.escalationData = escalationRates.mechLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolLaborEscalationType.SPECIALIST:
        escalationDialogData.escalationData = escalationRates.specLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolLaborEscalationType.PROGRAMMING:
        escalationDialogData.escalationData = escalationRates.progLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolLaborEscalationType.GRAPHICS:
        escalationDialogData.escalationData = escalationRates.grapLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      case SolLaborEscalationType.DIGITAL_DEPLOYMENT_SPECIALIST:
        escalationDialogData.escalationData = escalationRates.ddepLaborEscalation;
        this.solEstimateTableCommonFunctionsService.openEscalationDialog(escalationDialogData);
        break;
      default:
        break;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
